import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import type { Person } from '@/types/models';

export interface PersonFormData {
  full_name: string;
  email: string | null;
  employee_id: string | null;
  title: string | null;
  phone: string | null;
  notes: string | null;
  department_id: string | null;
  location_id: string | null;
}

// Fetch all people for an organization
export function usePeople(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['people', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      const { data, error } = await supabase
        .from('people')
        .select(
          `
          *,
          location:locations(id, name),
          department:departments(id, name)
        `
        )
        .eq('organization_id', organizationId)
        .order('full_name', { ascending: true });

      if (error) throw error;
      return data as Person[];
    },
    enabled: !!organizationId,
  });
}

// Fetch a single person with their assigned assets
export function usePerson(personId: string | undefined) {
  return useQuery({
    queryKey: ['people', personId],
    queryFn: async () => {
      if (!personId) throw new Error('Person ID is required');

      const { data, error } = await supabase
        .from('people')
        .select(
          `
          *,
          location:locations(id, name),
          department:departments(id, name),
          person_assets(
            id,
            assigned_at,
            asset:assets(
              id,
              description,
              tag_id,
              brand,
              model,
              serial_number,
              status
            )
          )
        `
        )
        .eq('id', personId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!personId,
  });
}

// Create a new person
export function useCreatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PersonFormData & { organization_id: string }) => {
      const { error, data: newPerson } = await supabase
        .from('people')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return newPerson;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      notifications.show({
        title: 'Success',
        message: 'Person created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create person',
        color: 'red',
      });
    },
  });
}

// Update an existing person
export function useUpdatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PersonFormData> }) => {
      const { error, data: updatedPerson } = await supabase
        .from('people')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return updatedPerson;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      notifications.show({
        title: 'Success',
        message: 'Person updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update person',
        color: 'red',
      });
    },
  });
}

// Delete a person
export function useDeletePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('people').delete().eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      notifications.show({
        title: 'Success',
        message: 'Person deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete person',
        color: 'red',
      });
    },
  });
}
